import React from 'react';
import SeedIcon from '../../../assets/seed-icon.svg';
import ModelTrainingIcon from '../../../assets/model-training-icon.svg';
import HandPlantIcon from '../../../assets/hand-plant.svg';

import SlideGraphics1a from '../../../assets/slide-graphics1-a 1.png';
import SlideGraphics1b from '../../../assets/slide-graphics-1b 1.png';

export default function Slide1() {
  return (
    <div className="animate-in slide-in-from-right duration-300 pt-6">
      {/* Main Heading */}
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
        Where Good Ideas Grow
      </h1>

      {/* Two Column Comparison with Side Images */}
      <div className="mb-0">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-center gap-4">
          {/* Left Image - Red */}
          <div className="flex-shrink-0 -ml-8">
            <img src={SlideGraphics1a} alt="Problem illustration" className="w-[110px] h-auto" />
          </div>

          {/* Content Grid */}
          <div className="flex-1 flex items-start gap-6">
            {/* Problem Column - Red */}
            <div className="flex-1 bg-white">
              <h2 className="text-base font-bold text-red-700 mb-3">
                Online discourse doesn't have to feel draining.
              </h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                The problem isn't that people are broken — it's that the systems we use were built
                to capture attention, not reward contribution.{' '}
                <span className="font-semibold">
                  They profit when we react, not when we reflect.
                </span>
              </p>
            </div>

            {/* Divider */}
            <div className="w-px bg-[#B4B4B4] self-stretch"></div>

            {/* Solution Column - Green */}
            <div className="flex-1 bg-white">
              <h2 className="text-base font-bold text-green-700 mb-3">
                Twygs takes a
                <br />
                different approach.
              </h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                Our system flips the incentives. The more you contribute meaningfully, the more
                value you create for yourself, for others, and for the ecosystem.{' '}
                <span className="font-semibold">
                  Because when good ideas are nurtured, they thrive.
                </span>
              </p>
            </div>
          </div>

          {/* Right Image - Green */}
          <div className="flex-shrink-0 -mr-8">
            <img src={SlideGraphics1b} alt="Solution illustration" className="w-[110px] h-auto" />
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden space-y-6">
          {/* Problem Section - Image + Text */}
          <div className="flex items-start gap-3 -mx-6 px-6">
            <img
              src={SlideGraphics1a}
              alt="Problem illustration"
              className="w-[100px] flex-shrink-0"
            />
            <div className="flex-1">
              <h2 className="text-base font-bold text-red-700 mb-2">
                Online discourse doesn't have to feel draining.
              </h2>
              <p className="text-gray-700 text-xs leading-relaxed">
                The problem isn't that people are broken — it's that the systems we use were built
                to capture attention, not reward contribution.{' '}
                <span className="font-semibold">
                  They profit when we react, not when we reflect.
                </span>
              </p>
            </div>
          </div>

          {/* Solution Section - Text + Image */}
          <div className="flex items-start gap-3 -mx-6 px-6">
            <div className="flex-1">
              <h2 className="text-base font-bold text-green-700 mb-2">
                Twygs takes a different approach.
              </h2>
              <p className="text-gray-700 text-xs leading-relaxed">
                Our system flips the incentives. The more you contribute meaningfully, the more
                value you create for yourself, for others, and for the ecosystem.{' '}
                <span className="font-semibold">
                  Because when good ideas are nurtured, they thrive.
                </span>
              </p>
            </div>
            <img
              src={SlideGraphics1b}
              alt="Solution illustration"
              className="w-[100px] flex-shrink-0"
            />
          </div>
        </div>
      </div>

      {/* What We're Cultivating Section */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
          What We're Cultivating
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-5 md:px-0">
          {/* Attention → Intention */}
          <div className="bg-green-50 rounded-xl border border-green-200">
            <div className="bg-green-500 text-white font-semibold text-sm py-1 px-4 rounded-t-xl mb-3 text-center">
              Attention → Intention
            </div>
            <div className="flex items-start space-x-3 py-1 px-4">
              <img src={SeedIcon} alt="Seed" className="w-8 h-8 flex-shrink-0 mt-1" />
              <p className="text-gray-700 text-sm leading-relaxed">
                You spend time online with purpose.
              </p>
            </div>
          </div>

          {/* Noise → Nourishment */}
          <div className="bg-green-50 rounded-xl border border-green-200">
            <div className="bg-green-500 text-white font-semibold text-sm py-1 px-4 rounded-t-xl mb-3 text-center">
              Noise → Nourishment
            </div>
            <div className="flex items-start space-x-3 py-1 px-4">
              <img src={HandPlantIcon} alt="Hand Plant" className="w-8 h-8 flex-shrink-0 mt-1" />
              <p className="text-gray-700 text-sm leading-relaxed">
                You engage with ideas that feed growth.
              </p>
            </div>
          </div>

          {/* Content → Connection */}
          <div className="bg-green-50 rounded-xl border border-green-200">
            <div className="bg-green-500 text-white font-semibold text-sm py-1 px-4 rounded-t-xl mb-3 text-center">
              Content → Connection
            </div>
            <div className="flex items-start space-x-3 py-1 px-4">
              <img
                src={ModelTrainingIcon}
                alt="Model Training"
                className="w-8 h-8 flex-shrink-0 mt-1"
              />
              <p className="text-gray-700 text-sm leading-relaxed">
                You contribute, learn, and grow with others.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Text */}
      <div className="text-center mt-8">
        <p className="text-gray-800 text-base font-bold leading-relaxed">
          Twygs is designed to turn your time online into something that grows —
          <br className="hidden md:block" />
          for you and for the people around you.
        </p>
      </div>
    </div>
  );
}
